import { useEffect, useMemo, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import { CONFIG } from '../../config'
import { getToken, getUsuarioInfo } from '../../services/localstorage/localstorage.service'
import { registerReadMessageId } from '../../services/chat/chat-unread.service'
import { errorHandler } from '../../services/util/error.service'
import * as DeliveryHttp from '../../services/api/https/deliveries/delivery.http'

const CHAT_ENDPOINT = `${CONFIG[CONFIG.ENVIROMENT].TP_DRIVE_END_POINT}/chat`
const SEND_TIMEOUT_MS = 10000
const MAX_MESSAGE_LENGTH = 1000

const INITIAL_STATE = {
  messages: [],
  messageText: '',
  isLoadingHistory: true,
  isSending: false,
  isConnected: false,
  noticeMessage: null,
}

export default function useRouteChat(fleetId) {
  // O servidor valida fleet_id como number estrito nos eventos do socket.
  const numericFleetId = Number(fleetId) || null
  const [state, setState] = useState({ ...INITIAL_STATE })
  const socketRef = useRef(null)
  const sendingRef = useRef(false)
  const user = useMemo(() => getUsuarioInfo(), [])

  const localDispatch = useMemo(() => {
    const appendMessages = novasMensagens => {
      setState(prevState => ({
        ...prevState,
        messages: mergeMessages(prevState.messages, novasMensagens),
      }))
    }

    return {
      // Helpers
      isOwnMessage(message) {
        return message.sender_type === 'tp' && Number(message.sender_id) === Number(user.id)
      },

      // Actions
      onChangeMessageText(messageText) {
        setState(prevState => ({ ...prevState, messageText, noticeMessage: null }))
      },

      async fetchHistory(fleet_id) {
        try {
          const data = await DeliveryHttp.getRouteChatByFleet(fleet_id)
          const history = data && Array.isArray(data.messages) ? data.messages : []
          setState(prevState => ({
            ...prevState,
            isLoadingHistory: false,
            messages: mergeMessages(history, prevState.messages),
          }))
        } catch (error) {
          setState(prevState => ({
            ...prevState,
            isLoadingHistory: false,
            noticeMessage: `Falha ao carregar o histórico do chat: ${errorHandler(error)}`,
          }))
        }
      },

      connect(fleet_id) {
        const socket = io(CHAT_ENDPOINT, {
          transports: ['websocket'],
          auth: {
            userType: 'tp',
            token: getToken(),
          },
        })
        socketRef.current = socket

        socket.on('connect', () => {
          socket.emit('chat:join', { fleet_id }, ack => {
            if (ack && ack.ok) {
              setState(prevState => ({ ...prevState, isConnected: true, noticeMessage: null }))
            } else {
              setState(prevState => ({
                ...prevState,
                isConnected: false,
                noticeMessage:
                  ack && ack.error ? ack.error : 'Não foi possível entrar no chat da rota.',
              }))
            }
          })
        })

        socket.on('chat:message', message => {
          if (!message || Number(message.fleet_id) !== Number(fleet_id)) return
          if (message.delivery_id !== null && message.delivery_id !== undefined) return
          appendMessages([message])
        })

        socket.on('disconnect', () => {
          setState(prevState => ({ ...prevState, isConnected: false }))
        })

        socket.on('connect_error', () => {
          setState(prevState => ({
            ...prevState,
            isConnected: false,
            noticeMessage: 'Não foi possível conectar ao chat em tempo real.',
          }))
        })

        return () => {
          if (socket.connected) socket.emit('chat:leave', { fleet_id }, () => {})
          socket.removeAllListeners()
          socket.disconnect()
          if (socketRef.current === socket) socketRef.current = null
          sendingRef.current = false
        }
      },

      sendMessage(fleet_id, text) {
        const message = (text || '').trim()
        if (!message || sendingRef.current) return

        if (message.length > MAX_MESSAGE_LENGTH) {
          setState(prevState => ({
            ...prevState,
            noticeMessage: `A mensagem deve ter no máximo ${MAX_MESSAGE_LENGTH} caracteres.`,
          }))
          return
        }

        const socket = socketRef.current
        if (!socket || !socket.connected) {
          setState(prevState => ({
            ...prevState,
            noticeMessage: 'Sem conexão com o chat. Aguarde a reconexão e tente novamente.',
          }))
          return
        }

        sendingRef.current = true
        setState(prevState => ({ ...prevState, isSending: true, noticeMessage: null }))

        const payload = { fleet_id: Number(fleet_id), message }
        socket.timeout(SEND_TIMEOUT_MS).emit('chat:send', payload, (timeoutError, ack) => {
          sendingRef.current = false

          if (timeoutError || !ack || !ack.ok) {
            setState(prevState => ({
              ...prevState,
              isSending: false,
              noticeMessage:
                ack && ack.error
                  ? ack.error
                  : 'Não foi possível enviar a mensagem. Tente novamente.',
            }))
            return
          }

          setState(prevState => ({
            ...prevState,
            isSending: false,
            messageText: '',
            messages: mergeMessages(prevState.messages, [ack.message]),
          }))
        })
      },
    }
  }, [user.id])

  useEffect(() => {
    if (!numericFleetId) return undefined

    setState({ ...INITIAL_STATE })
    localDispatch.fetchHistory(numericFleetId)
    return localDispatch.connect(numericFleetId)
  }, [numericFleetId, localDispatch])

  // Com o chat aberto, tudo que está na lista conta como lido (mensagens em ordem asc).
  useEffect(() => {
    if (!numericFleetId || !state.messages.length) return
    registerReadMessageId(numericFleetId, state.messages[state.messages.length - 1].id)
  }, [numericFleetId, state.messages])

  return { state, localDispatch, user }
}

function mergeMessages(currentMessages, incomingMessages) {
  const byId = new Map()
  currentMessages.forEach(message => byId.set(message.id, message))
  ;(incomingMessages || []).forEach(message => {
    if (message && message.id !== undefined && message.id !== null) byId.set(message.id, message)
  })
  return Array.from(byId.values()).sort((a, b) => a.id - b.id)
}
